import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { rejectionTypeToPolicyTypeMap, errorClassificationLookup } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import * as Sentry from '@sentry/react';

import { track } from 'core/helpers/analytics';
import { trackProspectFunnelEvent } from 'core/helpers/prospect-funnel';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { Banner } from 'core/components/banner';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { useQueryParams } from 'core/hooks/useQueryParams';
import { phoneNumberFormatter } from 'core/helpers/formatters';
import { isCanonicalUser } from 'core/helpers/troubleshooting-mode';
import { getNestedErrors } from 'quote/helpers/get-nested-errors';
import { updateUserInputWithConnectedHomeValues } from 'quote/helpers/get-connected-home-new-sign-up-values';
import { AddCar } from '../add-car';
import { ApplicantDetails } from '../applicant-details';
import { PersonalDetails } from '../personal-details';
import { AutoDetails } from '../auto-details';
import { Footer } from '../footer';
import { PriorAddress } from '../prior-address';
import { PropertyDetail } from '../property-detail';
import { PrimaryLocationDetails } from '../primary-location-details';
import { OfferDetails } from '../offer-details';
import { Disclaimer } from 'common/components/disclaimer';
import { getInitialValues } from './initial-values';
import { updateFormFields } from './update-form-fields';
import { handleCleanUp, codeSectionMatch } from './quote-forms.clean';
import { startSubmitProgressToasts } from './submit-progress-toasts';
import {
  shouldAlertBlockNavigation,
  focusFirstAlertField,
  buildQuotePrefill,
  formatCallerPhone,
  pinnedSectionsForPolicyType
} from './quote-forms.helpers';
import { useActiveScreenPop } from './use-active-screen-pop';
import { validateQuoteForm } from './quote-forms.validations';
import { OfferBranchPlusModal } from '../offer-branch-plus-modal';
import { ScenarioSimulationTools } from '../scenario-simulation-tools';
import useStyles from './quote-forms.styles';

const DISMISSIBLE_REJECTION_CODES = [50055, 50096, 50097, 50098];

const ScrollToError = ({ lastServerboundSubmitCount, onClientValidationError }) => {
  const { errors, isSubmitting, setFieldTouched, submitCount, values } = useFormikContext();
  const toast = useToast();
  const lastToastedSubmitCount = useRef(0);
  // Read at toast time only, so typing doesn't re-run the effect below
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    if (
      Object.keys(errors).length &&
      !isSubmitting &&
      submitCount > lastToastedSubmitCount.current &&
      submitCount !== lastServerboundSubmitCount.current
    ) {
      lastToastedSubmitCount.current = submitCount;
      onClientValidationError?.(Object.keys(errors), valuesRef.current);
      toast.notify({
        type: 'error',
        message:
          'We need a few more details to create this quote. Please complete the highlighted fields before resubmitting.'
      });
    }
  }, [errors, isSubmitting, submitCount, toast, lastServerboundSubmitCount, onClientValidationError]);

  // this useEffect is for when we click "resubmit offer with accurate details" on an offer that has alerts.
  // this will scroll to the already existing error alert, whereas the other hook in this file will scroll to alerts
  // that pop up after the form is submitted
  useEffect(() => {
    [
      'error-alert-applicant-details',
      'error-alert-offer-details',
      'error-alert-personal-details',
      'error-alert-prior-address',
      'error-alert-auto-details',
      'error-alert-add-cars',
      'error-alert-property-details'
    ].every((id) => {
      const element = document.getElementById(id);
      if (element) {
        // using set timeout because the first time this runs, the element isnt rendered yet so there is no
        // alert to scroll to, even though the element exists
        setTimeout(() => {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }, 100);
        return false;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length && isSubmitting) {
      const smoothScrollToElementById = (id) => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      };
      const errorPaths = getNestedErrors(errors);
      for (const path of errorPaths) {
        setFieldTouched(path);
      }

      // Function to map complex field paths to section error alert IDs
      const getErrorSectionId = (errorPath) => {
        // Map farm entity errors to their field IDs for direct field scrolling
        if (errorPath.startsWith('entity.')) {
          return errorPath;
        }
        // Map farm field errors to property details section
        if (errorPath.includes('farmLocations') || errorPath.includes('.risks[') || errorPath.includes('.building.')) {
          return 'error-alert-property-details';
        }
        // Add other field-to-section mappings as needed
        return errorPath; // Default: try original path as ID
      };

      try {
        if (errorPaths?.length > 1 && errorPaths[0] === 'fcraDisclaimer') {
          const sectionId = getErrorSectionId(errorPaths[1]);
          smoothScrollToElementById(sectionId);
        } else {
          const sectionId = getErrorSectionId(errorPaths[0]);
          smoothScrollToElementById(sectionId);
        }
      } catch {
        // something went wrong just scroll to top
        smoothScrollToElementById('firstName');
      }
    }
  }, [errors, isSubmitting, setFieldTouched]);
  return null;
};

const formatAdditionalPhoneNumbers = (additionalPhoneNumbers) => {
  return additionalPhoneNumbers
    .filter((phoneDetail) => phoneDetail.phoneNumber)
    .map((phoneDetail) => {
      return {
        phoneNumber: phoneNumberFormatter({ phoneNumber: phoneDetail.phoneNumber }),
        note: phoneDetail?.note ? phoneDetail.note.trim() : '',
        canText: phoneDetail.canText
      };
    });
};

const getOpenSections = (errors) => {
  return errors.reduce((openSections, error) => [...openSections, ...codeSectionMatch(Number(error.code))], []);
};

const QuoteForms = () => {
  const {
    quote: {
      requestQuoteAction,
      requestBranchPlusQuoteAction,
      getOfferDetails,
      errors,
      offer,
      loading,
      rejections,
      prefillData,
      prefillSource,
      fetchAffinityData,
      getBlocks,
      simulationConfig,
      setSimulationSource,
      toggleNoHitDataPull,
      resetVinFailureTracker
    },
    affinityLookups,
    account: { sendEventAsMember }
  } = useStore();

  const blocks = getBlocks();
  const history = useHistory();
  const classes = useStyles({ rejections: rejections.length, blocks: blocks.length });
  const form = useRef(null);
  // The submitCount whose validation passed and reached onSubmit — ScrollToError's toast is
  // only for attempts blocked client-side before any request fired. A single number (not a
  // Set): only the current count is ever compared, and it survives a Formik remount safely.
  const lastServerboundSubmitCount = useRef(0);
  const params = useQueryParams();
  const toast = useToast();

  const session = useSession();
  const { allStates, user, isAgency, canWriteMultipleAffinityCodes, unlicensedStates, allowedStates, isInternalSales } =
    session;
  // Derive the boolean here and depend on it: AuthContext's value is a fresh object each render,
  // so depending on `session` would rebuild handleSubmit on every Auth re-render.
  const isTroubleshooter = !isCanonicalUser(session);
  // Same reason: funnel events read the latest session through a ref instead of a dependency.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // Attempts blocked by Formik validation before any request fires (field names as the codes)
  const trackClientValidationError = useCallback(
    (invalidFields, values) =>
      track('Staff New Quote Error', {
        errorCategory: 'client-validation',
        errorCodes: invalidFields,
        requestedPolicyType: values?.policyType,
        state: values?.state,
        affinity: values?.affinity,
        requotedFromWebUserId: prefillSource?.webUserId,
        requotedFromOfferId: prefillSource?.offerId
      }),
    [prefillSource]
  );

  // these states added to remotely collapse / expand two of the collapsible cards based on the
  // value of isNewConstruction or isNewPurchase( only uses Prior Address)
  const [showPriorAddressCallout, setShowPriorAddressCallout] = useState(false);
  const [showPropertyDetailsCallout, setShowPropertyDetailsCallout] = useState(false);
  const [sectionsToShow, setSectionsToShow] = useState(errors ? getOpenSections(errors) : []);
  const [showAllSectionsClicked, setShowAllSectionsClicked] = useState(false);
  const [hasDisabledAffinity, setHasDisabledAffinity] = useState(false);
  const [requestedPolicyType, setRequestedPolicyType] = useState(null);
  const [showOfferBranchPlusModal, setShowOfferBranchPlusModal] = useState(false);
  const [branchPlusOnlyMode, setBranchPlusOnlyMode] = useState(false);

  // A Dialpad campaign pop opens /quote?affinity=… in a new tab, but agents often quote from the
  // New Offer header or an existing tab instead, losing the attribution. Fall back to the agent's
  // active pop when nothing else says which affinity this quote is for.
  const {
    activeScreenPop,
    loading: activeScreenPopLoading,
    dismiss: dismissActiveScreenPop
  } = useActiveScreenPop({ enabled: !params.affinity && !isAgency && !offer && !prefillData });
  const prefillAffinity = params.affinity || activeScreenPop?.affinity;

  useEffect(() => {
    if (prefillAffinity && !affinityLookups.loading) {
      affinityLookups.getByAffinity(prefillAffinity);
    }
  }, [affinityLookups, prefillAffinity]);

  const handleSubmit = useCallback(
    (branchPlusOnlyMode) => async (values, formikBag) => {
      lastServerboundSubmitCount.current = form.current?.submitCount ?? lastServerboundSubmitCount.current;
      setRequestedPolicyType(values?.policyType);
      // Set from requestQuoteAction's result when the request itself failed (not-licensed, network,
      // GraphQL errors). Wins over the navigation checks below, which can still see the previous
      // attempt's offer in the store after a failed request.
      let requestFailureCategory;
      // Staff-user-keyed: one event per quote attempt that ends without reaching an offer, with the
      // category (why) and every error code the store holds. Rater-side failures also appear in
      // the prospect-keyed Failed Offer; client-side blocks appear nowhere else.
      const trackNewQuoteError = (errorCategory, errorCodes = getOfferDetails().errorCodes) => {
        track('Staff New Quote Error', {
          errorCategory: requestFailureCategory || errorCategory,
          errorCodes: errorCodes.map(String),
          requestedPolicyType: values?.policyType,
          state: values?.state,
          affinity: values?.affinity,
          isBranchPlus: Boolean(branchPlusOnlyMode),
          requotedFromWebUserId: prefillSource?.webUserId,
          requotedFromOfferId: prefillSource?.offerId
        });
      };
      const affinityData = affinityLookups.data?.find(({ affinity }) => affinity === values.affinity);

      // track if unlicensed users are creating quotes
      if (unlicensedStates?.includes(values.state)) {
        track('Unlicensed user created quote', {
          agentUsername: user?.username,
          agentAffinityCode: user?.affinityCode,
          unlicensedStates,
          allowedStates,
          state: values.state,
          policyType: values.policyType,
          affinity: values.affinity
        });
      }
      if (values?.optIntoMarketingCampaigns) {
        track('Member Opted Into Marketing Campaigns', {
          email: values?.email,
          phone: values?.phone
        });
      }

      // send in values with connected home toggled on for affinity codes that are home security partners
      if (affinityData?.data.homeSecurity) {
        values = updateUserInputWithConnectedHomeValues(values, affinityData);
      }

      if (values.additionalPhoneNumbers?.length) {
        values.additionalPhoneNumbers = formatAdditionalPhoneNumbers(values.additionalPhoneNumbers);
      }

      if (values.phone?.length) {
        values.phone = phoneNumberFormatter({ phoneNumber: values.phone.toString() });
      }

      values.isApartment = Boolean(values.policyType?.includes('R'));

      // Branch Plus submits run as a long async task with their own button loading message;
      // the timed-out-retrying copy only applies to the regular requestQuote flow
      const cancelProgressToasts = branchPlusOnlyMode ? () => {} : startSubmitProgressToasts(toast.notify);

      // The Branch Plus modal calls handleSubmit(true)(values, { setFieldValue })
      // directly, bypassing Formik's validate pipeline — so the Yup
      // oneOf([true]) gate on fcraDisclaimer doesn't fire on that path.
      // Re-check at runtime so missing consent can't slip through.
      if (!values.fcraDisclaimer) {
        toast.notify({
          type: 'error',
          message: 'FCRA disclosure and consent is required to proceed.'
        });
        cancelProgressToasts();
        trackNewQuoteError('fcra-consent', []);
        return;
      }

      // Offers created by troubleshooters start flagged so they don't hijack the auto-redirect (#30994)
      if (isTroubleshooter) {
        values.global = { ...values.global, troubleshootingMode: true };
      }

      if (branchPlusOnlyMode) {
        requestFailureCategory = await requestBranchPlusQuoteAction(values, allStates);
      } else {
        requestFailureCategory = await requestQuoteAction(values, allStates);
      }

      toast.clean();
      cancelProgressToasts();

      handleCleanUp(
        errors,
        sectionsToShow,
        values.isNewConstruction,
        values.isNewPurchase,
        formikBag.setFieldValue,
        setSectionsToShow,
        showAllSectionsClicked,
        pinnedSectionsForPolicyType(values.policyType)
      );

      const { offer, alerts, blocks, rejections } = getOfferDetails();
      // The offer page is where the prospect-keyed funnel starts for Staff-originated quotes.
      const trackNewOfferGenerated = () =>
        trackProspectFunnelEvent({
          sendEventAsMember,
          eventName: 'Staff Funnel: New Offer Generated',
          offer,
          session: sessionRef.current,
          extraProps: {
            requestedPolicyType: values?.policyType,
            isBranchPlus: Boolean(branchPlusOnlyMode),
            isRequote: Boolean(prefillSource),
            requotedFromOfferId: prefillSource?.offerId,
            requotedFromWebUserId: prefillSource?.webUserId,
            // false = the requote minted a new identity, splitting the prospect in Amplitude
            requoteKeptWebUserId: prefillSource?.webUserId ? prefillSource.webUserId === offer?.webUserId : undefined
          }
        });

      // Cascade errors come back one at a time; put the agent on the exact field the code needs.
      // No-op when nothing maps or when we navigate away below.
      focusFirstAlertField(alerts, formikBag.setFieldTouched);

      if (branchPlusOnlyMode && offer?.branchPlusOffers?.length > 0 && rejections.length === 0) {
        if (offer.branchPlusOffers.some((offer) => offer.status === 'success')) {
          if (requestFailureCategory) {
            trackNewQuoteError(requestFailureCategory);
          } else {
            trackNewOfferGenerated();
          }
          history.push(`/offer/${offer.id}`);
          return;
        }
      }

      const hasNoValidPolicyTypes = values.policyType.split('').every((policyType) => {
        return rejections.some((rejection) => {
          const isRelevantPolicyType = rejectionTypeToPolicyTypeMap[rejection.type].includes(policyType);
          const isAlert = ['ALERT', 'DISMISSIBLE_REJECTION', 'BYPASS_ALERT'].includes(
            errorClassificationLookup[Number(rejection.code)]
          );
          return isRelevantPolicyType && rejection.level !== 'soft' && !isAlert;
        });
      });

      const branchPlusOnlyErrorsPresent = errors.some((error) => [5008, 5027].includes(Number(error.code)));
      if (
        (branchPlusOnlyErrorsPresent || hasNoValidPolicyTypes) &&
        !isAgency &&
        isInternalSales &&
        !branchPlusOnlyMode
      ) {
        setShowOfferBranchPlusModal(true);
      }

      // logic to fill out this form with data received back from rater
      if (offer?.quote) {
        updateFormFields(offer.quote, formikBag.setFieldValue, values);
      }

      // logic to redirect to modify offer page if successful offer received
      if (!offer?.options?.length > 0) {
        trackNewQuoteError(offer ? 'no-options' : 'no-offer');
        return;
      }
      if (alerts.length > 0) {
        // if any of the alerts are relevant to the requested policy type, then we should not push to offer
        const validAlerts = alerts.some((alert) => shouldAlertBlockNavigation(alert, values.policyType));

        if (validAlerts) {
          trackNewQuoteError('alert');
          return;
        }
      }
      if (blocks.length > 0) {
        trackNewQuoteError('block');
        return;
      }
      // if any of the rejections have a type that rejects our chosen policyType, then we should not push to offer
      const requestedPolicyTypeIsRejected = rejections?.some((rejection) => {
        if (rejection.level === 'soft' && !DISMISSIBLE_REJECTION_CODES.includes(rejection.type)) {
          return false;
        }
        return rejectionTypeToPolicyTypeMap[rejection.type]?.some((type) => type === values.policyType);
      });
      if (requestedPolicyTypeIsRejected) {
        trackNewQuoteError('rejection');
        return;
      }
      // A failed request can leave the previous attempt's offer in the store; navigation to it is
      // pre-existing, but it must not count as a new Staff entry (it would set prospectChannel).
      if (requestFailureCategory) {
        trackNewQuoteError(requestFailureCategory);
      } else {
        trackNewOfferGenerated();
      }
      history.push(`/offer/${offer.id}`);
    },
    [
      requestQuoteAction,
      requestBranchPlusQuoteAction,
      allStates,
      affinityLookups,
      history,
      getOfferDetails,
      errors,
      sectionsToShow,
      showAllSectionsClicked,
      setSectionsToShow,
      toast,
      user,
      allowedStates,
      unlicensedStates,
      isAgency,
      isTroubleshooter,
      isInternalSales,
      prefillSource,
      sendEventAsMember
    ]
  );

  const validate = useCallback(
    (values) => {
      const { alerts } = getOfferDetails();

      return validateQuoteForm(
        errors.map(({ code }) => Number(code)),
        values,
        allStates,
        isAgency,
        alerts,
        user
      )
        .then((result) => {
          return result;
        })
        .catch((e) => {
          // log so we can quickly see validation issues in production
          if (e?.name?.includes('TypeError')) {
            Sentry.captureException(e, {
              extra: { formValues: values, inNewQuoteForm: true, validationFailedToRun: true }
            });
          } else {
            console.warn('[QuoteForms] Validation errors blocking submission:', JSON.stringify(e, null, 2));
            return e;
          }
        });
    },
    [errors, allStates, isAgency, getOfferDetails, user]
  );

  const prefill = buildQuotePrefill({ offerQuote: offer?.quote, prefillData, params, activeScreenPop });
  const initialValues = getInitialValues(prefill, user, requestedPolicyType);
  const activeScreenPopBannerText =
    activeScreenPop &&
    [
      `Affinity ${activeScreenPop.affinity} prefilled from your Dialpad call`,
      activeScreenPop.callerPhone && `with ${formatCallerPhone(activeScreenPop.callerPhone)}`
    ]
      .filter(Boolean)
      .join(' ');

  // "Not this call": undo only what the pop filled in, keep anything the agent already changed.
  const undoActiveScreenPopPrefill = (values, setFieldValue) => {
    if (values.affinity === activeScreenPop.affinity) {
      setFieldValue('affinity', user?.affinityCode ?? '');
    }
    if (activeScreenPop.leadSource && values.leadSource === activeScreenPop.leadSource) {
      setFieldValue('leadSource', undefined);
    }
    dismissActiveScreenPop();
  };

  useEffect(() => {
    const affinityData = affinityLookups.data?.find(({ affinity }) => affinity === user.affinityCode);

    if (affinityData?.disabled) {
      setHasDisabledAffinity(true);
    }
  }, [affinityLookups.data, user.affinityCode]);

  useEffect(() => {
    if (canWriteMultipleAffinityCodes) {
      affinityLookups.getByAffinityList(user.viewableAffinityCodes, 'ALL');
    } else {
      fetchAffinityData(user.affinityCode);
    }
    // a fresh quote form starts a fresh VIN-failure history — a leftover count from an earlier
    // quote must not render "VIN still not found" on this quote's first failure
    resetVinFailureTracker();
  }, []);

  const showClearOffer = !!offer || branchPlusOnlyMode || !!prefillData;

  return (
    <div
      className={classNames(classes.container, {
        [classes.withRejections]: rejections.length > 0 || blocks.length > 0
      })}
    >
      {loading && <div className={classes.submittingOverlay} data-testid="quote-submitting-overlay" />}
      <div className={classes.row}>
        <Label type="title" className={classes.title}>
          Create a New Offer
        </Label>
        {showClearOffer && (
          <Button
            className={classes.floatingButton}
            variant="contained"
            color="secondary"
            type="button"
            // this will force the page to reload, clearing the form AND the quote store
            onClick={() => history.go(0)}
          >
            Clear this offer
          </Button>
        )}
      </div>
      {affinityLookups.loading || activeScreenPopLoading ? (
        <Loading noBackground />
      ) : (
        <Formik
          innerRef={form}
          initialValues={initialValues}
          onSubmit={handleSubmit(branchPlusOnlyMode)}
          validate={validate}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ values, setFieldValue, setFieldTouched }) => {
            return (
              <>
                {showOfferBranchPlusModal && (
                  <OfferBranchPlusModal
                    open={showOfferBranchPlusModal}
                    onClose={() => setShowOfferBranchPlusModal(false)}
                    submitBranchPlus={() => {
                      setShowOfferBranchPlusModal(false);
                      setBranchPlusOnlyMode(true);
                      handleSubmit(true)(values, { setFieldValue, setFieldTouched });
                    }}
                  />
                )}
                {/* native disabled fieldset also blocks keyboard edits while the submit is in flight */}
                <fieldset disabled={loading} className={classes.fieldset}>
                  {activeScreenPop && (
                    <div className={classes.screenPopBannerWrapper}>
                      <Banner show color="green" iconType="check">
                        <div className={classes.screenPopBanner}>
                          <Label type="smallWhite">{activeScreenPopBannerText}</Label>
                          <Button
                            variant="text"
                            color="secondary"
                            type="button"
                            onClick={() => undoActiveScreenPopPrefill(values, setFieldValue)}
                          >
                            Not this call
                          </Button>
                        </div>
                      </Banner>
                    </div>
                  )}
                  <ApplicantDetails
                    setShowPriorAddressCallout={setShowPriorAddressCallout}
                    setShowPropertyDetailsCallout={setShowPropertyDetailsCallout}
                    sectionsToShow={sectionsToShow}
                    setSectionsToShow={setSectionsToShow}
                    showAllSectionsClicked={showAllSectionsClicked}
                    setShowAllSectionsClicked={setShowAllSectionsClicked}
                    isAddressLocked={showClearOffer}
                  />
                  {/* Farm / Farm Home / Farm Rental - Offer Details */}
                  {isFarmBasedPolicyType(values.policyType) && <OfferDetails />}
                  <PersonalDetails sectionsToShow={sectionsToShow} />
                  <PriorAddress showPriorAddressCallout={showPriorAddressCallout} sectionsToShow={sectionsToShow} />
                  <AutoDetails sectionsToShow={sectionsToShow} />
                  <AddCar sectionsToShow={sectionsToShow} />

                  {/* Farm */}
                  <PrimaryLocationDetails sectionsToShow={sectionsToShow} />
                  {/* End Farm */}

                  {/* Home, Condo, Farm Property Details */}
                  <PropertyDetail
                    showPropertyDetailsCallout={showPropertyDetailsCallout}
                    sectionsToShow={sectionsToShow}
                  />
                  <ScenarioSimulationTools
                    simulationConfig={simulationConfig}
                    onSourceChange={setSimulationSource}
                    onToggleNoHitDataPull={toggleNoHitDataPull}
                  />

                  <Disclaimer />
                </fieldset>
                <ScrollToError
                  lastServerboundSubmitCount={lastServerboundSubmitCount}
                  onClientValidationError={trackClientValidationError}
                />
                <Footer
                  disableButton={Boolean(loading || affinityLookups.loading || hasDisabledAffinity)}
                  branchPlusOnlyMode={branchPlusOnlyMode}
                />
              </>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default observer(QuoteForms);
